import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import axios from "axios";
import {
    ArrowLeft,
    MailQuestion,
    AlertCircle,
    CheckCircle2,
    Loader2,
} from "lucide-react";
import TopNav from "../components/landing/TopNav";
import Footer from "../components/landing/Footer";

const REVEAL = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
};

const AUTH_API_URL =
    process.env.REACT_APP_AUTH_API_URL ||
    import.meta?.env?.REACT_APP_AUTH_API_URL ||
    "https://api.getbetween.us";

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());

export default function ForgotPassword() {
    const reduce = useReducedMotion();
    const { pathname } = useLocation();

    const [email, setEmail] = useState("");
    const [touched, setTouched] = useState(false);
    const [status, setStatus] = useState("idle"); // idle|sending|sent|error
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        document.title = "Forgot password — BetweenUs";
        window.scrollTo(0, 0);
    }, [pathname]);

    const error = !emailOk(email) ? "Enter a valid email address." : "";
    const isValid = !error;
    const anim = reduce ? {} : REVEAL;

    const onSubmit = async (e) => {
        e.preventDefault();
        setTouched(true);
        if (!isValid || status === "sending") return;
        setStatus("sending");
        setErrorMsg("");
        try {
            await axios.post(
                `${AUTH_API_URL}/api/auth/forgot-password`,
                { email: email.trim().toLowerCase() },
                { timeout: 15000 },
            );
            // Anti-enumeration: always show the same confirmation, even on error
            // we still surface the friendly message unless the network truly failed.
            setStatus("sent");
        } catch (err) {
            const isNetwork = !err?.response;
            if (isNetwork) {
                setErrorMsg(
                    "We couldn’t reach the server. Check your connection and try again.",
                );
                setStatus("error");
            } else {
                // Backend errors still resolve to the anti-enumeration message —
                // the user shouldn’t learn whether the email exists.
                setStatus("sent");
            }
        }
    };

    return (
        <div
            className="relative min-h-screen bg-paper text-ink flex flex-col"
            data-testid="forgot-password-page"
        >
            <TopNav variant="legal" />

            <main className="flex-1">
                <section
                    data-testid="forgot-password-hero"
                    className="relative overflow-hidden border-b border-brand"
                >
                    <div className="absolute inset-0 hero-wash pointer-events-none" />
                    <div className="noise-overlay" />
                    <div className="relative mx-auto w-full max-w-2xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-10">
                        <Link
                            to="/"
                            data-testid="forgot-back-home"
                            className="inline-flex items-center gap-2 text-[12.5px] font-medium tracking-[-0.01em] text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)] transition-colors"
                        >
                            <ArrowLeft size={14} strokeWidth={2} />
                            Back to home
                        </Link>

                        <motion.div
                            {...anim}
                            className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand bg-white/80 backdrop-blur px-3 py-1 text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--brand-teal-2)]"
                            data-testid="forgot-eyebrow"
                        >
                            <MailQuestion size={12} strokeWidth={2.2} />
                            Account · Forgot password
                        </motion.div>

                        <motion.h1
                            {...anim}
                            transition={{
                                ...(anim.transition || {}),
                                delay: 0.05,
                            }}
                            className="mt-4 font-serif text-[34px] sm:text-[42px] leading-[1.05] tracking-[-0.02em] text-[var(--brand-ink)]"
                            data-testid="forgot-title"
                        >
                            Forgot your password?
                        </motion.h1>
                        <motion.p
                            {...anim}
                            transition={{
                                ...(anim.transition || {}),
                                delay: 0.1,
                            }}
                            className="mt-3 text-[15.5px] leading-[1.6] text-[var(--brand-muted-2)] max-w-xl"
                            data-testid="forgot-subtitle"
                        >
                            Enter the email tied to your BetweenUs account.
                            We’ll send a reset link if it’s on file.
                        </motion.p>
                    </div>
                </section>

                <section className="relative">
                    <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
                        {status === "sent" ? (
                            <div
                                data-testid="forgot-success"
                                className="rounded-[18px] bg-white border border-brand shadow-[var(--shadow-soft)] p-6 sm:p-8"
                            >
                                <div className="flex items-start gap-3">
                                    <span
                                        className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(48,140,138,0.12)] text-[var(--brand-teal-2)]"
                                        aria-hidden="true"
                                    >
                                        <CheckCircle2
                                            size={18}
                                            strokeWidth={2}
                                        />
                                    </span>
                                    <div>
                                        <h2 className="font-serif text-[22px] tracking-[-0.01em] text-[var(--brand-ink)]">
                                            Check your inbox.
                                        </h2>
                                        <p className="mt-2 text-[14.5px] leading-[1.6] text-[var(--brand-muted-2)]">
                                            If an account exists for that
                                            email, we just sent a reset link.
                                            It can take a minute — and please
                                            check your spam folder.
                                        </p>
                                        <div className="mt-5 flex flex-wrap gap-3">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setStatus("idle");
                                                    setEmail("");
                                                    setTouched(false);
                                                }}
                                                data-testid="forgot-resend"
                                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-brand text-[13.5px] font-semibold tracking-[-0.01em] text-[var(--brand-ink)] hover:bg-[var(--brand-paper-2)] transition-colors"
                                            >
                                                Try a different email
                                            </button>
                                            <Link
                                                to="/"
                                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[var(--brand-navy)] text-[var(--brand-paper)] text-[13.5px] font-semibold tracking-[-0.01em] hover:bg-[var(--brand-navy-2)] transition-colors shadow-[0_10px_24px_rgba(14,27,42,0.14)] ring-focus"
                                            >
                                                Back to home
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <form
                                noValidate
                                onSubmit={onSubmit}
                                data-testid="forgot-form"
                                className="rounded-[18px] bg-white border border-brand shadow-[var(--shadow-soft)] p-6 sm:p-8 space-y-5"
                            >
                                <div>
                                    <label
                                        htmlFor="forgot-email"
                                        className="block text-[12.5px] font-semibold tracking-[0.02em] text-[var(--brand-ink)]"
                                    >
                                        Email address
                                    </label>
                                    <input
                                        id="forgot-email"
                                        name="email"
                                        type="email"
                                        inputMode="email"
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        onBlur={() => setTouched(true)}
                                        aria-invalid={
                                            touched && error ? true : undefined
                                        }
                                        aria-describedby={
                                            touched && error
                                                ? "forgot-email-error"
                                                : undefined
                                        }
                                        data-testid="forgot-input-email"
                                        placeholder="you@example.com"
                                        className={`mt-1.5 w-full h-11 rounded-[12px] border bg-white px-3.5 text-[14.5px] text-[var(--brand-ink)] placeholder:text-[var(--brand-muted)] outline-none transition-colors ring-focus ${
                                            touched && error
                                                ? "border-[rgba(220,38,38,0.45)]"
                                                : "border-brand focus:border-[var(--brand-teal-2)]"
                                        }`}
                                    />
                                    {touched && error && (
                                        <p
                                            id="forgot-email-error"
                                            className="mt-1.5 text-[12.5px] text-[#991B1B]"
                                        >
                                            {error}
                                        </p>
                                    )}
                                </div>

                                {status === "error" && errorMsg && (
                                    <div
                                        role="alert"
                                        data-testid="forgot-error"
                                        className="flex items-start gap-2.5 rounded-[12px] border border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.06)] px-3.5 py-3 text-[13.5px] leading-[1.55] text-[#991B1B]"
                                    >
                                        <AlertCircle
                                            size={16}
                                            strokeWidth={2}
                                            className="mt-0.5 shrink-0"
                                        />
                                        <span>{errorMsg}</span>
                                    </div>
                                )}

                                <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3">
                                    <button
                                        type="submit"
                                        disabled={
                                            status === "sending" ||
                                            (touched && !isValid)
                                        }
                                        data-testid="forgot-submit"
                                        className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[var(--brand-navy)] text-[var(--brand-paper)] text-[14px] font-semibold tracking-[-0.01em] shadow-[0_10px_24px_rgba(14,27,42,0.14)] hover:bg-[var(--brand-navy-2)] hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.98] transition-[background-color,transform,box-shadow] duration-200 ring-focus disabled:opacity-60 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
                                    >
                                        {status === "sending" ? (
                                            <>
                                                <Loader2
                                                    size={16}
                                                    className="animate-spin"
                                                />
                                                Sending…
                                            </>
                                        ) : (
                                            <>Send reset link</>
                                        )}
                                    </button>

                                    <Link
                                        to="/"
                                        data-testid="forgot-cancel"
                                        className="inline-flex items-center justify-center h-12 px-5 text-[13.5px] font-medium tracking-[-0.01em] text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)] transition-colors"
                                    >
                                        Back to sign in
                                    </Link>
                                </div>
                            </form>
                        )}

                        <p className="mt-6 text-[12.5px] text-[var(--brand-muted)]">
                            Need help? Email{" "}
                            <a
                                href="mailto:support@tylegn.com"
                                className="underline decoration-[var(--brand-teal-2)] underline-offset-2 hover:text-[var(--brand-ink)]"
                            >
                                support@tylegn.com
                            </a>
                            .
                        </p>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
